import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import {
  PalletCloseBulkRequest,
  PalletCloseBulkResponse,
  PalletCloseRequest,
  ReturnsService
} from '../../../services/returns.service';
import { NotificationService } from '../../../services/notification.service';
import {
  SearchableSelectComponent,
  SearchableSelectOption
} from '../../../components/searchable-select/searchable-select.component';

/** Dati minimi del reso da chiudere: i tre codici che lo individuano lato backend */
export interface CloseManualPallet {
  palletCode: string;
  palletWaybillCode: string;
  palletShipmentCode: string;
}

/**
 * Popup di chiusura manuale dei resi della pagina /returns.
 *
 * Mostra i tre codici di ogni bancale (reso, bolla, spedizione) e chiede il
 * magazzino di chiusura e una data di consegna con un flatpickr data+ora.
 * - I magazzini selezionabili arrivano da POST /pallets/close-warehouses: quelli
 *   autorizzati all'utente più la destinazione dei resi da chiudere. Viene
 *   preselezionato il magazzino proposto dal backend (la destinazione dei resi,
 *   altrimenti WHS00001).
 * - Con un solo reso invia codici, magazzino e data a POST /pallets/close, che
 *   valorizza la data di consegna e registra il quinto stato sul magazzino scelto;
 *   se il bancale ha già il quinto stato il backend risponde con errore e nulla
 *   viene scritto.
 * - Con più resi invia l'elenco, il magazzino e la data comuni a
 *   POST /pallets/close-bulk e mostra l'esito nella stessa forma dell'import CSV
 *   (chiusi, saltati, scartati).
 */
@Component({
  selector: 'app-close-manual-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, SearchableSelectComponent],
  templateUrl: './close-manual-popup.component.html',
  styleUrl: './close-manual-popup.component.css'
})
export class CloseManualPopupComponent implements OnChanges, OnDestroy {

  @Input() visible: boolean = false;
  /** Resi da chiudere: uno per la chiusura singola, più di uno per quella multipla */
  @Input() pallets: CloseManualPallet[] = [];

  @Output() closed = new EventEmitter<void>();
  /** Chiusura singola riuscita: bancale aggiornato restituito dal backend */
  @Output() palletClosed = new EventEmitter<any>();
  /** Chiusura multipla completata: esito restituito dal backend */
  @Output() bulkClosed = new EventEmitter<PalletCloseBulkResponse>();

  @ViewChild('deliveryDateInput') deliveryDateInput?: ElementRef<HTMLInputElement>;

  /** Data di consegna scelta nel picker (null finché non è valorizzata) */
  deliveryDate: Date | null = null;
  /** Magazzino di chiusura scelto nella tendina (vuoto finché non è valorizzato) */
  warehouseCode = '';
  /** Magazzini ammessi per la chiusura dei resi passati al popup */
  warehouseOptions: SearchableSelectOption[] = [];
  /** True mentre l'elenco dei magazzini viene caricato dal backend */
  isLoadingWarehouses = false;
  /** True durante l'invio al backend */
  isSubmitting = false;
  errorMessage = '';
  /** Esito della chiusura multipla; finché è valorizzato prende il posto del form */
  result: PalletCloseBulkResponse | null = null;

  private flatpickrInstance: FlatpickrInstance | null = null;

  constructor(private returnsService: ReturnsService,
              private notificationService: NotificationService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']) {
      if (this.visible) {
        this.resetState();
        this.loadWarehouses();
        // L'input esiste solo dopo il render dell'*ngIf: il picker va creato al tick successivo
        setTimeout(() => this.initPicker());
      } else {
        this.destroyPicker();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyPicker();
  }

  /** True quando i resi da chiudere sono più di uno */
  get isBulk(): boolean {
    return this.pallets.length > 1;
  }

  /**
   * True quando il popup è stato aperto dal bottone di chiusura multipla senza
   * alcun reso selezionato: al posto del form si mostra la guida.
   */
  get isEmptySelection(): boolean {
    return this.pallets.length === 0;
  }

  /** Flatpickr data+ora, stessa configurazione dei filtri data di damage-management */
  private initPicker(): void {
    const el = this.deliveryDateInput?.nativeElement;
    if (!el) {
      return;
    }
    this.destroyPicker();
    this.flatpickrInstance = flatpickr(el, {
      dateFormat: 'd/m/Y H:i',
      enableTime: true,
      time_24hr: true,
      locale: Italian,
      defaultDate: this.deliveryDate ?? undefined,
      onChange: (dates: Date[]) => {
        this.deliveryDate = dates[0] ?? null;
        this.errorMessage = '';
      }
    });
  }

  private destroyPicker(): void {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
      this.flatpickrInstance = null;
    }
  }

  private resetState(): void {
    this.deliveryDate = new Date();
    this.warehouseCode = '';
    this.warehouseOptions = [];
    this.isLoadingWarehouses = false;
    this.isSubmitting = false;
    this.errorMessage = '';
    this.result = null;
  }

  /**
   * Carica i magazzini tra cui scegliere quello di chiusura per i resi passati
   * al popup. Senza resi (popup aperto con la guida) non c'è nulla da caricare.
   * Preseleziona il magazzino proposto dal backend (destinazione dei resi o
   * WHS00001), altrimenti l'unico disponibile; con più scelte e nessuna
   * proposta lascia la decisione all'utente.
   */
  private loadWarehouses(): void {
    if (this.pallets.length === 0) {
      return;
    }
    this.isLoadingWarehouses = true;
    this.returnsService.getCloseWarehouses(this.pallets.map((p) => ({
      palletCode: p.palletCode,
      palletWaybillCode: p.palletWaybillCode,
      palletShipmentCode: p.palletShipmentCode
    }))).subscribe({
      next: (response) => {
        this.isLoadingWarehouses = false;
        this.warehouseOptions = (response?.warehouses ?? [])
          .filter((w) => !!w.warehouseCode)
          .map((w) => ({
            value: w.warehouseCode,
            label: w.warehouseName ? `${w.warehouseName} (${w.warehouseCode})` : w.warehouseCode
          }));
        const proposed = response?.defaultWarehouseCode ?? '';
        const hasProposed = !!proposed && this.warehouseOptions.some((o) => o.value === proposed);
        if (hasProposed) {
          this.warehouseCode = proposed;
        } else if (this.warehouseOptions.length === 1) {
          this.warehouseCode = this.warehouseOptions[0].value;
        } else {
          this.warehouseCode = '';
        }
      },
      error: (error) => {
        this.isLoadingWarehouses = false;
        this.warehouseOptions = [];
        this.errorMessage = this.describeError(error, 'Impossibile caricare i magazzini di chiusura');
      }
    });
  }

  /** Pulisce l'eventuale errore di validazione quando l'utente sceglie un magazzino */
  onWarehouseChange(): void {
    this.errorMessage = '';
  }

  openPicker(): void {
    this.flatpickrInstance?.open();
  }

  /** Imposta la data di consegna a adesso */
  setNow(): void {
    this.deliveryDate = new Date();
    this.flatpickrInstance?.setDate(this.deliveryDate, false);
    this.errorMessage = '';
  }

  /** Svuota la data scelta */
  clearDate(): void {
    this.deliveryDate = null;
    this.flatpickrInstance?.clear();
  }

  get canSubmit(): boolean {
    return this.pallets.length > 0
      && !!this.warehouseCode
      && !!this.deliveryDate
      && !this.isSubmitting
      && !this.isLoadingWarehouses;
  }

  trackByPalletCode(_index: number, p: CloseManualPallet): string {
    return `${p.palletCode}|${p.palletWaybillCode}|${p.palletShipmentCode}`;
  }

  onClose(): void {
    if (this.isSubmitting) {
      return;
    }
    this.closed.emit();
  }

  onSubmit(): void {
    if (this.pallets.length === 0 || !this.warehouseCode) {
      this.errorMessage = 'Seleziona il magazzino di chiusura';
      return;
    }
    if (!this.deliveryDate) {
      this.errorMessage = 'Seleziona la data di consegna';
      return;
    }
    if (this.isBulk) {
      this.submitBulk(this.deliveryDate);
    } else {
      this.submitSingle(this.pallets[0], this.deliveryDate);
    }
  }

  /** Chiusura di un solo reso: POST /pallets/close */
  private submitSingle(pallet: CloseManualPallet, date: Date): void {
    const request: PalletCloseRequest = {
      palletCode: pallet.palletCode,
      palletWaybillCode: pallet.palletWaybillCode,
      palletShipmentCode: pallet.palletShipmentCode,
      palletActualDeliveryDate: this.toOffsetIso(date),
      palletWarehouseCode: this.warehouseCode
    };

    this.isSubmitting = true;
    this.errorMessage = '';

    this.returnsService.closePallet(request).subscribe({
      next: (updated) => {
        this.isSubmitting = false;
        this.notificationService.success(`Reso ${request.palletCode} chiuso con successo`);
        this.palletClosed.emit(updated);
        this.closed.emit();
      },
      error: (error) => {
        this.isSubmitting = false;
        this.errorMessage = this.describeError(error);
      }
    });
  }

  /** Chiusura di più resi con la stessa data: POST /pallets/close-bulk */
  private submitBulk(date: Date): void {
    const request: PalletCloseBulkRequest = {
      palletActualDeliveryDate: this.toOffsetIso(date),
      palletWarehouseCode: this.warehouseCode,
      pallets: this.pallets.map((p) => ({
        palletCode: p.palletCode,
        palletWaybillCode: p.palletWaybillCode,
        palletShipmentCode: p.palletShipmentCode
      }))
    };

    this.isSubmitting = true;
    this.errorMessage = '';

    this.returnsService.closePalletsBulk(request).subscribe({
      next: (result) => {
        this.isSubmitting = false;
        this.result = result;
        if (result.appliedRows > 0) {
          this.notificationService.success(result.appliedRows === 1
            ? '1 reso chiuso con successo'
            : `${result.appliedRows} resi chiusi con successo`);
        } else {
          this.notificationService.warning('Nessun reso è stato chiuso');
        }
        // La pagina rilegge subito i dati: l'esito resta a video finché l'utente chiude il popup
        this.bulkClosed.emit(result);
      },
      error: (error) => {
        this.isSubmitting = false;
        this.errorMessage = this.describeError(error);
      }
    });
  }

  // ─── ESITO DELLA CHIUSURA MULTIPLA ───

  get hasErrorRows(): boolean {
    return (this.result?.errorRows ?? 0) > 0;
  }

  get hasSkippedRows(): boolean {
    return (this.result?.skippedRows ?? 0) > 0;
  }

  /** True se tutti i resi inviati sono stati chiusi */
  get isFullyApplied(): boolean {
    return !!this.result && !this.hasErrorRows && !this.hasSkippedRows;
  }

  get resultTitle(): string {
    if (!this.result) {
      return '';
    }
    if (this.result.appliedRows === 0) {
      return 'Nessun reso chiuso';
    }
    return this.isFullyApplied
      ? 'Resi chiusi correttamente'
      : 'Resi chiusi, con alcuni elementi da controllare';
  }

  /**
   * Reso a cui si riferisce una voce degli elenchi. Il backend non fissa il
   * nome del campo, quindi si tenta con quelli plausibili (stessa logica del
   * close-returns-popup).
   */
  rowLabel(item: any): string {
    if (item == null || typeof item !== 'object') {
      return '';
    }
    const code = item.palletCode ?? item.code;
    if (code) {
      return code;
    }
    const index = item.index ?? item.position ?? item.rowNumber ?? item.row;
    return index != null ? `Elemento ${index}` : '';
  }

  /** Motivo per cui un elemento è stato saltato o scartato */
  rowReason(item: any): string {
    if (item == null) {
      return '';
    }
    if (typeof item === 'string') {
      return item;
    }
    return item.reason ?? item.message ?? item.error ?? item.description
      ?? item.motivo ?? JSON.stringify(item);
  }

  /**
   * Formatta la data come OffsetDateTime ISO con l'offset locale del browser
   * (es. "2026-09-07T10:30:00+02:00"), il formato atteso dal backend.
   * Non si usa toISOString(): darebbe l'ora in UTC con suffisso Z.
   */
  private toOffsetIso(date: Date): string {
    const pad = (n: number) => String(Math.abs(n)).padStart(2, '0');
    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const offset = `${sign}${pad(Math.floor(Math.abs(offsetMinutes) / 60))}:${pad(Math.abs(offsetMinutes) % 60)}`;
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
      + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${offset}`;
  }

  /**
   * Messaggio leggibile dall'errore HTTP, preferendo quello restituito dal backend.
   * @param fallback testo per gli errori senza messaggio né stato riconosciuto
   */
  private describeError(error: any, fallback = 'Errore durante la chiusura dei resi'): string {
    const backendMessage = error?.error?.message
      || (typeof error?.error === 'string' ? error.error : '')
      || error?.error?.error;
    if (backendMessage) {
      return backendMessage;
    }
    switch (error?.status) {
      case 401: return 'Non autorizzato';
      case 403: return 'Non hai i permessi per chiudere i resi';
      case 404: return 'Reso non trovato';
      case 409: return 'Il reso è già stato chiuso';
      case 0: return 'Impossibile contattare il server';
      default: return fallback;
    }
  }
}
